// IRON CONDOR · Strategy Builder (Iron Condor plan Rev 2, Phase 2). READ-ONLY analysis of ONE condor on any
// NFO / MCX option chain: four legs priced live at their depth, every figure from the worker
// (iron_condor_math), model Greeks / POP clearly labelled (iron_condor_model), broker basket margin from
// Zerodha's read-only calculator, and saved configurations. There is no order action in this phase.
import { beDistanceText } from "@/lib/breakeven";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import api, { apiError } from "@/lib/api";
import { pct, rupees } from "@/components/options/PairValuation";
import { age, px, qty } from "@/components/options/OptionsCommon";
import { CONTROL, FilterField } from "@/components/options/ScannerTable";
import IcPayoffChart from "@/components/iron_condor/IcPayoffChart";
import { marginText, Tile } from "@/components/iron_condor/IcDetail";
import LiveCreate from "@/components/iron_condor/LiveCreate";

export const LEG_DEFS = [
  { leg: 1, side: "BUY", type: "PE", label: "Long put (wing)" },
  { leg: 2, side: "SELL", type: "PE", label: "Short put" },
  { leg: 3, side: "SELL", type: "CE", label: "Short call" },
  { leg: 4, side: "BUY", type: "CE", label: "Long call (wing)" },
];
const sideCls = (s) => (s === "BUY" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF3B30]");
const unit = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));
const money = (r, u) => (r !== null && r !== undefined ? rupees(r) : u !== null && u !== undefined ? `${unit(u)} / unit` : "—");
const signed = (v, f) => (v === null || v === undefined ? "—" : <span className={v >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"}>{f(v)}</span>);

/** K1 < K2 < K3 < K4, from the chain's own put / call strike lists. */
export function validStrikes(k) {
  return k.length === 4 && k.every((x) => typeof x === "number" && Number.isFinite(x) && x > 0)
    && k[0] < k[1] && k[1] < k[2] && k[2] < k[3];
}

/** Every leg has a strike (any order, overlap or repeat is allowed: the user may change side, type and strike). */
export function legsComplete(k) {
  return k.length === 4 && k.every((x) => typeof x === "number" && Number.isFinite(x) && x > 0);
}

/** Theoretical EXPIRY P&L per unit of ANY legs (display mirror of iron_condor_math.pnl_unit_legs). */
export function legsPnlUnit(legs, net, s) {
  return legs.reduce((t, x) => t + (x.side === "BUY" ? 1 : -1)
    * (x.option_type === "CE" ? Math.max(s - x.strike, 0) : Math.max(x.strike - s, 0)), net);
}

/** Theoretical EXPIRY P&L per unit (display mirror of iron_condor_math.pnl_per_unit - the worker is the source). */
export function expiryPnlUnit([k1, k2, k3, k4], c, s) {
  return c - Math.max(k2 - s, 0) + Math.max(k1 - s, 0) - Math.max(s - k3, 0) + Math.max(s - k4, 0);
}

/** A breakeven as a signed % of the reference price (the quote's spot), or null without a spot. */
export function breakevenPct(be, spot) {
  if (be == null || !spot) return null;
  return ((Number(be) / Number(spot)) - 1) * 100;
}

/** "145,894.50 (+0.10%)" for each breakeven; "none" without one. */
export function breakevenText(list, spot) {
  const xs = (list || []).filter((b) => b != null);
  if (!xs.length) return "none";
  return xs.map((b) => {
    const p = breakevenPct(b, spot);
    return `${Number(b).toLocaleString("en-IN", { maximumFractionDigits: 2 })}${p == null ? "" : ` (${p >= 0 ? "+" : ""}${p.toFixed(2)}%)`}`;
  }).join(" · ");
}

/** Underlying prices for the P&L table: the four strikes, both breakevens, spot and spot ±1 / ±2 / ±5 %. */
export function tablePrices(strikes, metrics, spot) {
  const xs = new Set(strikes);
  if (metrics?.breakeven_low != null) xs.add(metrics.breakeven_low);
  if (metrics?.breakeven_high != null) xs.add(metrics.breakeven_high);
  if (spot) [0, -1, 1, -2, 2, -5, 5].forEach((p) => xs.add(Math.round(spot * (1 + p / 100) * 100) / 100));
  return [...xs].sort((a, b) => a - b);
}

const STD_LEGS = LEG_DEFS.map((d) => ({ side: d.side, type: d.type }));

/** The saved / seeded legs' sides and types (a strategy saved before custom legs is a standard condor). */
function legsOf(x) {
  return Array.isArray(x?.legs) && x.legs.length === 4 ? x.legs.map((l) => ({ side: l.side, type: l.option_type })) : STD_LEGS;
}

function step(list, current, dir) {
  const i = list.indexOf(current);
  if (i < 0) return list[0];
  return list[Math.min(list.length - 1, Math.max(0, i + dir))];
}

function LegRow({ def, strike, strikes, quote, onChange, onFlip, cover }) {
  const q = quote || {};
  const ok = q.status === "OK";
  return (
    <div className="grid grid-cols-[2.2rem_3rem_2.4rem_1fr] gap-x-2 gap-y-1 items-center py-2 border-t border-[var(--term-border)]" data-testid={`ic-leg-${def.leg}`}>
      <span className="text-[11px] mono text-[var(--term-muted)]">L{def.leg}</span>
      <button type="button" title="Switch BUY / SELL" onClick={() => onFlip("side")} data-testid={`ic-leg-${def.leg}-side`}
        className={`text-center px-1 rounded-sm text-[10px] font-bold ${sideCls(def.side)}`}>{def.side}</button>
      <button type="button" title="Switch CE / PE" onClick={() => onFlip("type")} data-testid={`ic-leg-${def.leg}-type`}
        className="text-center px-1 rounded-sm text-[10px] font-bold bg-[#2563EB]/15 text-[#2563EB]">{def.type}</button>
      <span className="flex items-center gap-1">
        <button type="button" className="h-7 w-6 inline-flex items-center justify-center rounded-sm border border-[var(--term-border-strong)]"
          onClick={() => onChange(step(strikes, strike, -1))} aria-label={`L${def.leg} lower strike`}><ChevronLeft className="h-3 w-3" /></button>
        <select className={`${CONTROL} h-7 w-28 mono`} value={strike ?? ""} onChange={(e) => onChange(Number(e.target.value))} data-testid={`ic-leg-${def.leg}-strike`}>
          {strike == null && <option value="">strike</option>}
          {strikes.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <button type="button" className="h-7 w-6 inline-flex items-center justify-center rounded-sm border border-[var(--term-border-strong)]"
          onClick={() => onChange(step(strikes, strike, 1))} aria-label={`L${def.leg} higher strike`}><ChevronRight className="h-3 w-3" /></button>
        <span className="text-[10px] text-[var(--term-muted)] ml-1 hidden xl:inline">{def.label}</span>
        {cover && <span className={`text-[10px] font-bold ml-1 ${cover === "UNCOVERED" ? "text-[#FF3B30]" : "text-[var(--term-muted)]"}`}
          data-testid={`ic-leg-${def.leg}-cover`}>{cover}</span>}
      </span>
      <span />
      <span className="col-span-3 text-[11px] mono flex flex-wrap gap-x-3 text-[var(--term-secondary)]">
        <span>bid {px(q.bid)}</span><span>ask {px(q.ask)}</span>
        <span className="font-bold text-[var(--term-primary)]">{def.side === "SELL" ? "sell" : "buy"} @ {unit(q.price)}</span>
        <span>age {age(q.quote_age_s)}</span>
        {q.iv != null && <span title="model: implied from the mid">IV {(q.iv * 100).toFixed(1)}%</span>}
        {!ok && q.status && <span className="text-[#FF3B30] font-bold">{q.status}</span>}
      </span>
    </div>
  );
}

export default function IcBuilder({ seed, running, settings, trading = false, onCreated }) {
  const [unders, setUnders] = useState([]);
  const [cfg, setCfg] = useState({ exchange: "NFO", underlying: "", expiry: null, lots: 1, strikes: [null, null, null, null], legs: STD_LEGS });
  const [chain, setChain] = useState(null);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const [loadedId, setLoadedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [margin, setMargin] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!trading) return;
    api.get("/iron-condor/status").then((r) => { setShadow(r.data.shadow); setLive(r.data.live || null); })
      .catch(() => { setShadow(null); setLive(null); });
  }, [trading]);
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  useEffect(() => { optionsApi.get("/ic/underlyings").then((r) => setUnders(r.data.underlyings || [])).catch(() => {}); }, []);
  const loadSaved = useCallback(() => optionsApi.get("/ic/strategies").then((r) => setSaved(r.data.strategies || [])).catch(() => {}), []);
  useEffect(() => { loadSaved(); }, [loadSaved]);
  useEffect(() => {
    if (seed) { setCfg({ exchange: seed.exchange, underlying: seed.underlying, expiry: seed.expiry, lots: seed.lots || 1, strikes: seed.strikes, legs: legsOf(seed) }); setLoadedId(seed.id || null); setName(seed.name || ""); }
  }, [seed]);

  // the chain: strikes, lot, units, a suggested condor (pins the underlying + ATM pair while open)
  useEffect(() => {
    if (!running || !cfg.underlying) { setChain(null); return undefined; }
    let alive = true;
    const load = () => optionsApi.get("/ic/builder/chain", { params: { exchange: cfg.exchange, underlying: cfg.underlying, ...(cfg.expiry ? { expiry: cfg.expiry } : {}) } })
      .then((r) => {
        if (!alive) return;
        setChain(r.data); setErr(null);
        const c = cfgRef.current;
        if (!c.expiry && r.data.expiry) setCfg((x) => ({ ...x, expiry: r.data.expiry }));
        if (c.strikes.some((k) => k == null) && r.data.suggested) setCfg((x) => ({ ...x, strikes: r.data.suggested }));
      })
      .catch((e) => { if (alive) { setChain(null); setErr(optionsError(e)); } });
    load();
    const t = setInterval(() => { if (!document.hidden && cfgRef.current.strikes.some((k) => k == null)) load(); }, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [running, cfg.exchange, cfg.underlying, cfg.expiry]);

  const ready = running && cfg.underlying && legsComplete(cfg.strikes);
  const legDefs = cfg.legs.map((l, i) => ({ side: l.side, option_type: l.type, strike: cfg.strikes[i] }));
  const body = useMemo(() => ({ exchange: cfg.exchange, underlying: cfg.underlying, expiry: cfg.expiry, lots: cfg.lots, strikes: cfg.strikes,
    legs: cfg.legs.map((l, i) => ({ side: l.side, option_type: l.type, strike: cfg.strikes[i] })) }), [cfg]);
  useEffect(() => {
    setMargin(null);
    if (!ready) { setQuote(null); return undefined; }
    let alive = true;
    const load = () => optionsApi.post("/ic/builder/quote", body)
      .then((r) => { if (alive) { setQuote(r.data); setErr(null); } })
      .catch((e) => { if (alive) { setQuote(null); setErr(optionsError(e)); } });
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [ready, body]);

  const setStrike = (i, k) => setCfg((c) => { const s = [...c.strikes]; s[i] = k; return { ...c, strikes: s }; });
  // a leg's side or type flips; a strike not listed for the new type moves to the nearest listed one
  const flip = (i, what) => setCfg((c) => {
    const legs = c.legs.map((l, j) => (j !== i ? l : what === "side" ? { ...l, side: l.side === "BUY" ? "SELL" : "BUY" }
      : { ...l, type: l.type === "CE" ? "PE" : "CE" }));
    const list = legs[i].type === "PE" ? (chain?.put_strikes || []) : (chain?.call_strikes || []);
    const s = [...c.strikes];
    if (list.length && s[i] != null && !list.includes(s[i])) s[i] = list.reduce((a, b) => (Math.abs(b - s[i]) < Math.abs(a - s[i]) ? b : a), list[0]);
    return { ...c, legs, strikes: s };
  });
  const reset = () => { if (chain?.suggested) setCfg((c) => ({ ...c, strikes: chain.suggested, legs: STD_LEGS })); };
  const pickUnderlying = (exchange, underlying) => { setCfg({ exchange, underlying, expiry: null, lots: cfg.lots, strikes: [null, null, null, null], legs: STD_LEGS }); setLoadedId(null); setName(""); };
  const getMargin = async () => {
    setBusy(true);
    try { setMargin((await optionsApi.post("/ic/builder/margin", body)).data); }
    catch (e) { toast.error(optionsError(e)); }
    finally { setBusy(false); }
  };
  const save = async (asNew) => {
    try {
      const payload = { ...body, name: name.trim() || undefined };
      const r = loadedId && !asNew ? await optionsApi.put(`/ic/strategies/${loadedId}`, payload) : await optionsApi.post("/ic/strategies", payload);
      setLoadedId(r.data.id); setName(r.data.name); toast.success(`Saved "${r.data.name}"`); loadSaved();
    } catch (e) { toast.error(optionsError(e)); }
  };
  const createTrade = async (mode = "SHADOW") => {
    if (!quote?.metrics) return;
    const what = quote.structure === "CUSTOM" ? `custom 4-leg ${legDefs.map((l) => `${l.side} ${l.option_type} ${l.strike}`).join(" · ")}`
      : `Iron Condor ${cfg.strikes.join("/")}`;
    if (mode === "SHADOW" && !window.confirm(`Create a PLANNED SHADOW ${cfg.underlying} ${what} × ${cfg.lots} lot(s)? Nothing is sent until you start its entry.`)) return;
    setBusy(true);
    try {
      await api.post("/iron-condor/trades", { mode, exchange: quote.exchange, underlying: quote.underlying, expiry: quote.expiry,
        lots: cfg.lots, strikes: cfg.strikes, legs: legDefs, tradingsymbols: quote.legs.map((l) => l.tradingsymbol),
        plan_prices: quote.legs.map((l) => l.price), quoted_at: quote.generated_at, name: name.trim() || undefined });
      toast.success(`${mode} trade created (PLANNED)`);
      if (onCreated) onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (s) => {
    try { await optionsApi.delete(`/ic/strategies/${s.id}`); if (s.id === loadedId) setLoadedId(null); loadSaved(); }
    catch (e) { toast.error(optionsError(e)); }
  };

  const m = quote?.metrics;
  const md = quote?.model;
  const g = md?.greeks?.net;
  const cap = margin || quote?.capital;
  const names = unders.filter((u) => u.exchange === cfg.exchange);
  const putStrikes = chain?.put_strikes || [];
  const callStrikes = chain?.call_strikes || [];
  const legQuote = (i) => (quote?.legs?.[i] ? { ...quote.legs[i], iv: md?.leg_iv?.[i] } : null);
  const custom = quote?.structure === "CUSTOM";
  const coverOf = (i) => {
    if (!m?.coverage || cfg.legs[i].side !== "SELL") return null;
    const c = m.coverage[i];
    return c == null ? "UNCOVERED" : `covered by L${c + 1}`;
  };
  const unl = (v, r, u) => (v === "UNLIMITED" ? "UNLIMITED" : money(r, u));
  const qUnits = quote?.units ? quote.quantity * quote.units : null;

  if (!running) return <div className="term-panel rounded-sm p-6 text-sm text-[var(--term-secondary)]" data-testid="ic-builder-off">Start the Iron Condor scanner (top right): the Builder streams its four legs on the same feed.</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,420px)_1fr] gap-3" data-testid="ic-builder">
      <div className="space-y-3">
        <div className="term-panel rounded-sm p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-end">
            <FilterField label="Exchange"><select className={CONTROL} value={cfg.exchange} onChange={(e) => pickUnderlying(e.target.value, "")} data-testid="ic-b-exchange">
              <option>NFO</option><option>MCX</option></select></FilterField>
            <FilterField label="Underlying"><input list="ic-b-unders" className={`${CONTROL} w-36`} defaultValue={cfg.underlying} key={`${cfg.exchange}${cfg.underlying}`}
              placeholder="any listed" onBlur={(e) => e.target.value && e.target.value.toUpperCase() !== cfg.underlying && pickUnderlying(cfg.exchange, e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="ic-b-underlying" />
              <datalist id="ic-b-unders">{names.map((u) => <option key={u.underlying} value={u.underlying} />)}</datalist></FilterField>
            <FilterField label="Expiry"><select className={CONTROL} value={cfg.expiry || ""} onChange={(e) => setCfg((c) => ({ ...c, expiry: e.target.value, strikes: [null, null, null, null] }))} data-testid="ic-b-expiry">
              {(chain?.expiries || (cfg.expiry ? [cfg.expiry] : [])).map((x) => <option key={x}>{x}</option>)}</select></FilterField>
            <FilterField label="Lots (all legs)"><input type="number" min={1} max={1000} className={`${CONTROL} w-20 mono text-right`} value={cfg.lots}
              onChange={(e) => setCfg((c) => ({ ...c, lots: Math.max(1, Math.min(1000, Math.floor(Number(e.target.value) || 1))) }))} data-testid="ic-b-lots" /></FilterField>
          </div>
          {chain && <div className="text-[11px] text-[var(--term-secondary)] mono" data-testid="ic-b-chain">
            {chain.underlying_symbol} ({chain.underlying_rule}) spot {px(chain.spot)} · lot {qty(chain.lot_size)}
            {chain.units ? ` × ${chain.units} units` : " · units unverified"} · tick {chain.tick_size} · {chain.model === "BLACK_76" ? "Black-76" : "Black-Scholes"}</div>}
          {err && <div className="text-xs text-[#FF3B30]" data-testid="ic-b-error">{err}</div>}
          {quote && <div className="text-xs font-bold" data-testid="ic-b-structure">{custom ? "Custom 4-leg strategy" : "Iron Condor"}
            <span className="font-normal text-[var(--term-muted)]"> · click BUY/SELL or CE/PE to change a leg</span></div>}
          {chain && LEG_DEFS.map((d, i) => {
            const def = { ...d, side: cfg.legs[i].side, type: cfg.legs[i].type, label: custom ? "" : d.label };
            return <LegRow key={d.leg} def={def} strike={cfg.strikes[i]} strikes={def.type === "PE" ? putStrikes : callStrikes}
              quote={legQuote(i)} onChange={(k) => setStrike(i, k)} onFlip={(w) => flip(i, w)} cover={coverOf(i)} />;
          })}
          {quote && (
            <div className={`rounded-sm px-2 py-1.5 text-xs font-bold mono ${m ? "bg-[#00C805]/10 text-[#00C805]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`} data-testid="ic-b-net">
              {m ? `Net ${m.net_credit_unit < 0 ? "debit" : "credit"} ${money(m.net_credit == null ? null : Math.abs(m.net_credit), Math.abs(m.net_credit_unit))} (${unit(Math.abs(m.net_credit_unit))} / unit × ${qty(quote.quantity)}${quote.units ? ` × ${quote.units}` : ""})`
                : `Not priced: ${quote.refusal?.code}${quote.refusal?.legs ? ` ${Object.entries(quote.refusal.legs).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}`}
            </div>)}
          {m?.uncovered_legs?.length > 0 && <div className="text-[11px] font-bold text-[#FF3B30]" data-testid="ic-b-uncovered">
            UNCOVERED SELL on L{m.uncovered_legs.join(", L")}: no same-type BUY covers it{m.max_loss_unlimited ? " — the loss is UNLIMITED" : ""}.
            Entry sends it last, after every BUY fills, only with broker margin; an unlimited loss needs a rupee stop loss.</div>}
          {quote?.warnings?.length > 0 && <div className="text-[11px] text-[#b38f00]" data-testid="ic-b-warnings">⚠ {quote.warnings.join(" · ")}</div>}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={reset} disabled={!chain?.suggested} className="h-8 px-2 rounded-sm border border-[var(--term-border-strong)] text-xs inline-flex items-center gap-1 disabled:opacity-40">
              <RotateCcw className="h-3 w-3" />Reset</button>
            <input className={`${CONTROL} flex-1 min-w-[8rem]`} placeholder="name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="ic-b-name" />
            <button type="button" disabled={!legsComplete(cfg.strikes)} onClick={() => save(false)} data-testid="ic-b-save"
              className="h-8 px-2 rounded-sm bg-[#2563EB] text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40"><Save className="h-3 w-3" />{loadedId ? "Update" : "Save"}</button>
            {loadedId && <button type="button" onClick={() => save(true)} className="h-8 px-2 rounded-sm border border-[var(--term-border-strong)] text-xs">Save as new</button>}
          </div>
          {trading ? (
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="ic-b-shadow">
              <button type="button" disabled={busy || !quote?.metrics || !shadow?.account_ready} onClick={() => createTrade("SHADOW")} data-testid="ic-b-create-shadow"
                className="h-8 px-3 rounded-sm bg-[#7C3AED] text-white text-xs font-bold disabled:opacity-40">Create SHADOW trade</button>
              <LiveCreate live={live} disabled={busy || !quote?.metrics || quote?.stale} underlying={cfg.underlying} testId="ic-b-live"
                onConfirm={() => createTrade("LIVE")}
                lines={quote ? [`${quote.exchange}:${quote.underlying} ${quote.expiry} · ${cfg.lots} lot(s) × ${qty(quote.quantity)}`,
                  ...(quote.legs || []).map((l) => `L${l.leg} ${l.side} ${l.option_type} ${l.strike} @ ${unit(l.price)}`),
                  m ? `Net ${m.net_credit_unit < 0 ? "debit" : "credit"} ${unit(Math.abs(m.net_credit_unit))}/unit · max loss ${m.max_loss_unlimited ? "UNLIMITED" : money(m.max_loss, m.max_loss_unit)}` : "",
                  `Broker margin: ${marginText(cap)}`] : []} />
              <span className="text-[10px] text-[var(--term-muted)]">{shadow?.account_ready ? `SHADOW: real orders to the unfunded account, simulated fills${live?.allowed ? "" : " · LIVE not enabled for this account"}`
                : `SHADOW not ready: ${(shadow?.reasons || ["module off"]).join(", ")}`}</span>
            </div>)
            : <div className="text-[10px] text-[var(--term-muted)]" data-testid="ic-b-no-orders">Analysis only — SHADOW trading is not enabled in this build; LIVE is not available.</div>}
        </div>
        <div className="term-panel rounded-sm p-3" data-testid="ic-saved">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)] pb-1">Saved strategies ({saved.length})</div>
          {!saved.length && <div className="text-xs text-[var(--term-muted)]">None yet.</div>}
          {saved.map((s) => (
            <div key={s.id} className="flex items-center gap-2 py-1 border-t border-[var(--term-border)] text-xs" data-testid={`ic-saved-${s.id}`}>
              <button type="button" className="flex-1 text-left hover:underline" onClick={() => { setCfg({ exchange: s.exchange, underlying: s.underlying, expiry: s.expiry, lots: s.lots, strikes: s.strikes, legs: legsOf(s) }); setLoadedId(s.id); setName(s.name); }}>
                <b>{s.name}</b> <span className="mono text-[var(--term-muted)]">{s.exchange}:{s.underlying} {s.expiry || "nearest"} · {s.strikes.join("/")} · {s.lots}L</span>
                {s.expired && <span className="ml-1 text-[#FF3B30] font-bold">EXPIRED</span>}</button>
              <button type="button" onClick={() => remove(s)} aria-label={`Delete ${s.name}`} className="p-1 hover:text-[#FF3B30]"><Trash2 className="h-3 w-3" /></button>
            </div>))}
        </div>
      </div>

      <div className="space-y-3 min-w-0">
        {quote && <div className="flex flex-wrap gap-2" data-testid="ic-b-metrics">
          <Tile label="Max profit · expiry" value={unl(m?.max_profit_unit, m?.max_profit, m?.max_profit_unit)} sub={m?.max_profit_net != null ? `net of est. charges ${rupees(m.max_profit_net)}` : null} tone="good" />
          {custom ? <>
            <Tile label="Max loss · expiry" value={m?.max_loss_unlimited ? "UNLIMITED" : m && m.max_loss_unit <= 0 ? "none" : money(m?.max_loss, m?.max_loss_unit)} tone="bad" testId="ic-b-maxloss" />
            <Tile label="Min expiry P&L" value={m?.max_loss_unlimited ? "UNLIMITED loss" : money(m?.min_pnl, m?.min_pnl_unit)} testId="ic-b-minpnl"
              sub={m?.min_pnl_net != null ? `after est. charges ${rupees(m.min_pnl_net)} · theoretical, before execution and liquidation risk` : "theoretical expiry minimum"} />
            <Tile label="Est. charges" value={m?.costs != null ? rupees(m.costs) : "—"} sub="open + close, every leg" />
            <Tile label="Breakevens" value={breakevenText(m?.breakevens, quote?.spot)} testId="ic-b-breakevens"
              sub={quote?.spot ? `% vs ${quote.underlying_symbol || quote.underlying || "spot"} ${Number(quote.spot).toLocaleString("en-IN")}` : "% needs an underlying price"} />
          </> : <>
            <Tile label="Max loss" value={money(m?.max_loss, m?.max_loss_unit)} sub={m?.max_loss_net != null ? `incl. est. charges ${rupees(m.max_loss_net)}` : null} tone="bad" testId="ic-b-maxloss" />
            <Tile label="Breakevens · expiry (lower · upper)" testId="ic-b-breakevens"
              value={m ? `${breakevenText([m.breakeven_low], null)} · ${breakevenText([m.breakeven_high], null)}` : "—"}
              sub={quote?.spot ? `${beDistanceText(quote.spot, m?.breakeven_low, m?.breakeven_high) || "—"} from ${quote.underlying_symbol || quote.underlying || "spot"} ${Number(quote.spot).toLocaleString("en-IN")} · not a stop-loss`
                : "distance needs an underlying price"} />
            <Tile label="Return on risk" value={pct(m?.return_on_risk_pct)} sub={m ? `risk : reward ${m.risk_reward}` : null} />
            <Tile label="Widths P / C" value={m ? `${m.put_width} / ${m.call_width}` : "—"} sub={m ? (m.symmetric ? "symmetric" : "asymmetric") : null} />
          </>}
          <Tile label="Broker margin" value={marginText(cap)} testId="ic-b-margin"
            sub={cap?.return_on_margin_pct != null ? `return on margin ${pct(cap.return_on_margin_pct)}` : cap?.error || "Zerodha basket calculator"} />
          {custom && <Tile label="Return on capital" testId="ic-b-roc"
            value={m?.min_pnl_net != null && cap?.capital_required ? pct((m.min_pnl_net / cap.capital_required) * 100) : "—"}
            sub="min net expiry P&L / broker basket capital" />}
          {!custom && <Tile label="POP · model" value={md?.pop != null ? `${(md.pop * 100).toFixed(1)}%` : "unavailable"} testId="ic-b-pop"
            sub={md?.atm_iv != null ? `ATM IV ${(md.atm_iv * 100).toFixed(1)}% @ ${md.atm_strike}` : md?.reason || "needs a two-sided ATM book"} />}
        </div>}
        {quote && <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy || !m} onClick={getMargin} data-testid="ic-b-margin-btn"
            className="h-7 px-3 rounded-sm text-[11px] font-bold border border-[var(--term-border-strong)] hover:border-[#2563EB] disabled:opacity-40">
            {busy ? "Asking Zerodha…" : "Get broker margin (read-only calculator)"}</button>
          <span className="text-[10px] text-[var(--term-muted)]">oldest quote {age(quote.oldest_quote_age_s)}{quote.stale ? " · STALE" : ""}</span>
        </div>}
        {md && <div className="term-panel rounded-sm p-2" data-testid="ic-b-greeks">
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)] pb-1">Net Greeks · model estimate ({md.model === "BLACK_76" ? "Black-76" : "Black-Scholes"}, IV implied from each leg's mid, r {md.risk_free_rate_pct}%)</div>
          {g ? <div className="flex flex-wrap gap-4 text-xs mono">
            <span>Δ {unit(g.delta, 2)} <span className="text-[var(--term-muted)]">units</span></span>
            <span>Γ {unit(g.gamma, 4)}</span>
            <span>Θ {signed(g.theta_day, (v) => `${rupees(v)}/day`)}</span>
            <span>Vega {signed(g.vega_pt, (v) => `${rupees(v)}/IV pt`)}</span>
          </div> : md.greeks?.net_units ? <div className="text-xs mono">per unit: Δ {unit(md.greeks.net_units.delta, 4)} · Θ {unit(md.greeks.net_units.theta_day, 4)}/day · Vega {unit(md.greeks.net_units.vega_pt, 4)} (₹ needs verified units)</div>
            : <div className="text-xs text-[var(--term-muted)]">Unavailable: {md.reason || "a leg has no two-sided book to imply its volatility"}</div>}
        </div>}
        {md?.near_spot?.length > 0 && <div className="term-panel rounded-sm p-2 text-xs mono flex flex-wrap gap-4" data-testid="ic-b-near-spot">
          <span className="text-[10px] uppercase tracking-wide text-[var(--term-muted)] w-full">P&amp;L now · MODEL estimate (not expiry, not executable)</span>
          {md.near_spot.map((r) => <span key={r.pct}>spot {r.pct > 0 ? "+" : ""}{r.pct}% ({unit(r.s)}): {signed(r.pnl ?? r.pnl_unit, (v) => (r.pnl != null ? rupees(v) : `${v.toFixed(2)}/u`))}</span>)}
        </div>}
        {quote?.payoff?.curve?.length > 0 && <div className="term-panel rounded-sm p-2 min-w-0"><IcPayoffChart payoff={quote.payoff}
          markers={custom ? legDefs.map((l, i) => ({ x: l.strike, label: `L${i + 1} ${l.side} ${l.option_type} ${l.strike}` })) : null} /></div>}
        {m && <div className="term-panel rounded-sm p-2 overflow-auto" data-testid="ic-b-pnl-table">
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)] pb-1">P&amp;L at expiry by underlying price (theoretical, before charges)</div>
          <table className="text-xs mono w-full"><thead><tr className="text-[var(--term-muted)]"><th className="text-left">Underlying</th><th className="text-right">vs spot</th><th className="text-right">P&amp;L</th></tr></thead>
            <tbody>{tablePrices(custom ? cfg.strikes : m.strikes, m, quote.spot).map((s) => {
              const u = custom ? legsPnlUnit(legDefs, m.net_credit_unit, s) : expiryPnlUnit(m.strikes, m.net_credit_unit, s);
              return (<tr key={s} className="border-t border-[var(--term-border)]"><td>{Number(s).toLocaleString("en-IN")}</td>
                <td className="text-right">{quote.spot ? `${(((s / quote.spot) - 1) * 100).toFixed(2)}%` : "—"}</td>
                <td className="text-right">{signed(qUnits ? u * qUnits : u, (v) => (qUnits ? rupees(v) : `${v.toFixed(2)}/u`))}</td></tr>);
            })}</tbody></table>
        </div>}
        {!quote && chain && legsComplete(cfg.strikes) && <div className="text-xs text-[var(--term-muted)]">Pricing…</div>}
        {!cfg.underlying && <div className="term-panel rounded-sm p-6 text-sm text-[var(--term-secondary)]">Pick an exchange and any listed underlying — every NFO and MCX option chain is available.</div>}
      </div>
    </div>
  );
}
